// src/index.tsx
import React from "react";

// 🛡️ SECURITY FIX: The ultimate shield against 'default' policy conflicts.
// This monkey-patches the TrustedTypes API to intercept any duplicate creation attempts
// from browser extensions (LastPass, Dashlane, etc.) before they can crash the app.
if (typeof window !== 'undefined' && (window as any).trustedTypes) {
  try {
    const tt = (window as any).trustedTypes;
    if (tt.createPolicy && !tt._isPatched) {
      const originalCreate = tt.createPolicy.bind(tt);
      tt.createPolicy = function(name: string, rules: any) {
        if (name === 'default') {
          const names = tt.getPolicyNames ? tt.getPolicyNames() : [];
          if (names.includes('default') || tt.defaultPolicy) {
            console.log('🛡️ TrustedTypes: Intercepted duplicate "default" policy creation to prevent crash.');
            return tt.defaultPolicy || originalCreate('temp-' + Math.random(), rules);
          }
        }
        return originalCreate(name, rules);
      };
      tt._isPatched = true;
    }
  } catch (e) {
    console.warn('🛡️ TrustedTypes patch failed:', e);
  }
}

// Suppress global errors for this specific conflict to prevent the React Error Overlay
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.message && (
      event.message.includes('Policy with name "default" already exists') ||
      event.message.includes('TrustedTypePolicyFactory')
    )) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }, true);
}

import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "react-query";
// Styles globaux (body, --font-family, Lexend + fallbacks) — doit charger avant le reste pour éviter une serif système aléatoire.
import "./index.css";
import App from "./App";
import "./styles/tokens.css";
import "./styles/dev-night-mode-variables.css";
import "./styles/dev-night-mode-palettes.css";
import "./styles/dev-night-mode-pages.css";
import "./styles/dev-night-mode-planning-calendar.css";
import { Provider } from "react-redux";
import { createStore, combineReducers } from "redux";
import { PostHogProvider } from "posthog-js/react";
import posthog from 'posthog-js';
import {
  initBackofficePostHog,
  isPostHogSupportedPlatform,
  optInBackofficePostHog,
  optOutBackofficePostHog,
} from './analytics';
import * as Sentry from "@sentry/react";
import { AuthProvider } from './context/AuthContext';
import { DynamicMuiThemeProvider } from './theme';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MessagingConfigProvider } from './contexts/MessagingConfigContext';
import { ConsentProvider, useConsent } from './contexts/ConsentContext';
import MetaPixelGate from './components/MetaPixelGate';
import { LanguageProvider } from './contexts/LanguageContext';
import { HelmetProvider } from './components/SEO/helmet';
// Initialize i18n
import './config/i18n'; 
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import { installChunkErrorHandler } from './utils/chunkErrorHandler';
import {
  SENTRY_IGNORE_ERROR_PATTERNS,
  createSentryBeforeSend,
} from './utils/sentryEventFilter';
import { scheduleStripMarketingSeoShellAfterReactHero } from './utils/stripMarketingSeoShell';
import { DEV_NIGHT_MODE_STORAGE_KEY, DevNightModeProvider } from './hooks/useDevNightMode';
import { DEV_NIGHT_PALETTE_STORAGE_KEY, parseDevNightPaletteId } from './theme/devNightPalettes';

// Aligner <html> sur la préférence mode nuit avant le premier paint (MagicLoader / auth sans header).
if (typeof document !== 'undefined') {
  try {
    if (localStorage.getItem(DEV_NIGHT_MODE_STORAGE_KEY) === '1') {
      document.documentElement.setAttribute('data-dev-night-mode', 'on');
      document.documentElement.setAttribute(
        'data-night-palette',
        parseDevNightPaletteId(localStorage.getItem(DEV_NIGHT_PALETTE_STORAGE_KEY))
      );
    }
  } catch {
    /* navigation privée / quota */
  }
}

// Capture les ChunkLoadError dus à un déploiement pendant la navigation
// (un nouveau hash de chunk remplace l'ancien). Au lieu d'écran blanc,
// on recharge proprement la page une seule fois.
installChunkErrorHandler();

// Create a root reducer combining all reducers (even if empty)
const rootReducer = combineReducers({
  // Add other reducers here if needed
  dummy: (state = {}) => state,
});

// Initialize Sentry
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // PII Data
    sendDefaultPii: true,
    ignoreErrors: SENTRY_IGNORE_ERROR_PATTERNS,
    beforeSend: createSentryBeforeSend(),
  });
  console.log("✅ Sentry initialized");
} else {
  console.warn("⚠️ Sentry DSN not found - Error tracking disabled");
}

const store = createStore(rootReducer);

// React Query : cache partagé pour listes/détails (ex. réservations)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 min : pas de refetch automatique pendant la nav
      cacheTime: 5 * 60 * 1000, // 5 min en mémoire après désactivation
    },
  },
});

// SEO statique build : retiré après le 1er H1 React (pas avant — #1118 v2 /en/ mobile)
scheduleStripMarketingSeoShellAfterReactHero();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const PostHogGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { consent } = useConsent();
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!isPostHogSupportedPlatform()) {
      console.log('📱 iOS natif détecté - PostHog désactivé');
      optOutBackofficePostHog();
      return;
    }

    if (consent === 'denied') {
      optOutBackofficePostHog();
      return;
    }

    if (consent !== 'granted') return;

    const apiKey = process.env.REACT_APP_POSTHOG_KEY || '';
    if (!apiKey) return;

    if (!initialized) {
      initBackofficePostHog(apiKey);
      setInitialized(true);
      return;
    }

    optInBackofficePostHog();
  }, [consent, initialized]);

  if (consent === 'granted' && initialized) {
    return (
      <PostHogProvider client={posthog}>
        {children}
      </PostHogProvider>
    );
  }
  return <>{children}</>;
};

root.render(
  <HelmetProvider>
  <GlobalErrorBoundary>
    <AuthProvider>
      <DevNightModeProvider>
      <LanguageProvider>
        <DynamicMuiThemeProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MessagingConfigProvider>
              <ConsentProvider>
                <MetaPixelGate>
                  <PostHogGate>
                    <Provider store={store}>
                      <QueryClientProvider client={queryClient}>
                        <App />
                      </QueryClientProvider>
                    </Provider>
                  </PostHogGate>
                </MetaPixelGate>
              </ConsentProvider>
            </MessagingConfigProvider>
          </LocalizationProvider>
        </DynamicMuiThemeProvider>
      </LanguageProvider>
      </DevNightModeProvider>
    </AuthProvider>
  </GlobalErrorBoundary>
  </HelmetProvider>
);
